import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { Tag, TagsResponse } from '../../lib/types';
import { SettingsCard } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { IconButton, RefreshIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Tag management.
 *
 * Tags decide which technician can take which job, so a fragmented catalog is an
 * operational problem, not a tidiness one. Two things get top billing: skills
 * orders require that nobody carries — work that cannot be assigned to anyone —
 * and tags in use that the catalog does not know about, which is what a bulk
 * import leaves behind.
 *
 * Merge is the repair. Renaming a slug is deliberately not offered: that would
 * orphan every reference to it, so changing what a tag *is* means merging it.
 *
 * This lives under territory settings rather than in the main navigation. Tags
 * are configured once and then left alone; giving them a place beside the board
 * and the day's orders overstated how often anyone opens them.
 */
export function TagsPage({ territoryId }: { territoryId: string | null }) {
  const ui = useUi();
  const [data, setData] = useState<TagsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    if (!territoryId) return;
    setLoading(true);
    try {
      setData(await api.tags(territoryId));
    } catch (err) {
      ui.notify('error', 'Could not load tags', err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [territoryId, ui]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(() => data?.tags.filter((t) => !t.archived) ?? [], [data]);

  const create = async () => {
    if (!territoryId || !newName.trim()) return;
    try {
      await api.createTag(territoryId, { name: newName });
      setNewName('');
      ui.notify('ok', 'Tag created');
      await load();
    } catch (err) {
      ui.notify('error', 'Could not create tag', err instanceof ApiError ? err.message : String(err));
    }
  };

  const merge = async (source: Tag, targetId: string) => {
    if (!territoryId) return;
    const target = active.find((t) => t.id === targetId);
    if (!target) return;

    const references =
      source.technician_count + source.vehicle_count + source.required_count + source.forbidden_count;
    const ok = await ui.confirm({
      title: `Merge ${source.label} into ${target.label}?`,
      body:
        `${references} reference${references === 1 ? '' : 's'} will be rewritten to "${target.slug}" ` +
        `and "${source.slug}" will be removed. This cannot be undone.`,
      confirmLabel: 'Merge',
    });
    if (!ok) return;

    setBusySlug(source.slug);
    try {
      const result = await api.mergeTag(territoryId, source.id, targetId);
      ui.notify(
        'ok',
        `Merged into ${target.label}`,
        `${result.technicians} technicians, ${result.vehicles} vehicles, ${result.orders} orders updated.`,
      );
      await load();
    } catch (err) {
      ui.notify('error', 'Merge failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusySlug(null);
    }
  };

  const remove = async (tag: Tag) => {
    if (!territoryId) return;
    const references =
      tag.technician_count + tag.vehicle_count + tag.required_count + tag.forbidden_count;
    const ok = await ui.confirm({
      title: references > 0 ? `Archive ${tag.label}?` : `Delete ${tag.label}?`,
      body:
        references > 0
          ? `It is still used ${references} time${references === 1 ? '' : 's'}, so it will be archived rather than deleted. Merge it into another tag if you want it gone entirely.`
          : 'Nothing uses this tag, so it will be removed.',
      confirmLabel: references > 0 ? 'Archive' : 'Delete',
    });
    if (!ok) return;

    try {
      await api.deleteTag(territoryId, tag.id);
      ui.notify('ok', references > 0 ? 'Tag archived' : 'Tag deleted');
      await load();
    } catch (err) {
      ui.notify('error', 'Could not remove tag', err instanceof ApiError ? err.message : String(err));
    }
  };

  const reconcile = async () => {
    if (!territoryId) return;
    try {
      const result = await api.reconcileTags(territoryId);
      ui.notify(
        'ok',
        result.count > 0 ? `Registered ${result.count} tag${result.count === 1 ? '' : 's'}` : 'Nothing to register',
        result.count > 0 ? result.registered.join(', ') : undefined,
      );
      await load();
    } catch (err) {
      ui.notify('error', 'Could not reconcile', err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="view">
      <SettingsCard
        title="Tags"
        description="Skills and equipment that decide who can take which job"
        actions={
          <IconButton label="Refresh tags" onClick={() => void load()} disabled={loading}>
            {loading ? <span className="spinner" /> : <RefreshIcon />}
          </IconButton>
        }
        editing={false}
      >
      {data && data.unregistered.length > 0 ? (
        <div className="banner" data-tone="warn">
          <div>
            <b>{data.unregistered.length} tag{data.unregistered.length === 1 ? '' : 's'} in use but not registered</b>
            <p>
              {data.unregistered.join(', ')} — these came from a direct import and are affecting
              route planning without appearing in the catalog.
            </p>
          </div>
          <button type="button" className="btn btn-sm" onClick={() => void reconcile()}>
            Register
          </button>
        </div>
      ) : null}

      {data && data.unassignable.length > 0 ? (
        <div className="banner" data-tone="late">
          <div>
            <b>{data.unassignable.length} required skill{data.unassignable.length === 1 ? '' : 's'} nobody carries</b>
            <p>
              {data.unassignable.map((t) => t.label).join(', ')} — orders need these, so they cannot
              be assigned to anyone until a technician or vehicle carries them.
            </p>
          </div>
        </div>
      ) : null}

      <div className="tag-new">
        <input
          type="text"
          value={newName}
          placeholder="Add a tag, e.g. Gas Safe"
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void create();
          }}
          aria-label="New tag name"
        />
        <button type="button" className="btn btn-primary" onClick={() => void create()} disabled={!newName.trim()}>
          Add
        </button>
      </div>

      {active.length === 0 && !loading ? (
        <div className="empty">No tags yet.</div>
      ) : (
        <div className="tag-list">
          {active.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              others={active.filter((t) => t.id !== tag.id)}
              busy={busySlug === tag.slug}
              onMerge={(targetId) => void merge(tag, targetId)}
              onRemove={() => void remove(tag)}
            />
          ))}
        </div>
      )}

      {data && data.tags.some((t) => t.archived) ? (
        <>
          <h3 className="section-label">Archived</h3>
          <div className="tag-list">
            {data.tags
              .filter((t) => t.archived)
              .map((tag) => (
                <div key={tag.id} className="tag-row" data-archived="true">
                  <div className="tag-main">
                    <b>{tag.label}</b>
                    <small className="mono">{tag.slug}</small>
                  </div>
                  <span className="delta" data-tone="idle">archived</span>
                </div>
              ))}
          </div>
        </>
      ) : null}
      </SettingsCard>
    </div>
  );
}

function TagRow({
  tag,
  others,
  busy,
  onMerge,
  onRemove,
}: {
  tag: Tag;
  others: Tag[];
  busy: boolean;
  onMerge: (targetId: string) => void;
  onRemove: () => void;
}) {
  const carriers = tag.technician_count + tag.vehicle_count;
  const demand = tag.required_count;
  const unassignable = demand > 0 && carriers === 0;
  const orphan = carriers === 0 && demand === 0 && tag.forbidden_count === 0;

  return (
    <div className="tag-row" data-alert={unassignable ? 'true' : undefined}>
      <div className="tag-main">
        <b>{tag.label}</b>
        <small className="mono">{tag.slug}</small>
        {tag.auto_created ? <span className="delta" data-tone="idle">auto</span> : null}
      </div>

      <div className="tag-usage">
        <span title="Technicians carrying this skill">{tag.technician_count} tech</span>
        <span title="Vehicles carrying this skill">{tag.vehicle_count} veh</span>
        <span
          title="Orders requiring this skill"
          className="delta"
          data-tone={unassignable ? 'late' : orphan ? 'idle' : 'ok'}
        >
          {demand} orders
        </span>
      </div>

      <div className="tag-actions">
        {others.length > 0 ? (
          <Select
            value={null}
            placeholder={busy ? 'Merging' : 'Merge into'}
            options={others.map((t) => ({ value: t.id, label: t.label, hint: t.slug }))}
            onChange={onMerge}
            align="right"
          />
        ) : null}
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRemove} aria-label={`Remove ${tag.label}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
